import { noop } from 'lodash';
import React, { Component } from 'react';
import detectClickOutside from 'react-click-outside';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { navy, purple, grey25, grey75, white } from '@zocdoc/branding/colors';

import {
    INPUT_BORDER_WIDTH,
    INPUT_HEIGHT,
    NON_INPUT_X_AFFORDANCE_DESKTOP,
    NON_INPUT_X_AFFORDANCE_MOBILE,
} from '../../../picker-constants';

import { FontFamily } from '../../../typography';
import { breakpoint } from '../../../grid';
import { CARRIER } from '../../constants';
import { Textbox } from '../../../textbox';
import { Tooltip, TOOLTIP } from '../../../Tooltip';
import InsurancePickerDropdown from '../InsurancePickerDropdown';
import SelectedState from './SelectedState';
import { getPlaceholder } from '../../helpers/textbox-helpers';
import { Breakpoints } from '../../../grid/constants';
import { handleKeyChange } from '../HandleKeys';
import { Question } from '../../../icons';

const ErrorIcon = props => {
    const anchor = (
        <Question
            {...props}
            color={purple}
            data-test="insurance-error-icon"
            height={25}
        />
    );
    return (
        <StyledTooltip
            anchor={anchor}
            data-test="error-message-tooltip"
            showTooltipOnFocus
            showTooltipOnHover
            tooltipPosition={TOOLTIP.BOTTOM}
        >
            Please choose both a carrier and plan
        </StyledTooltip>
    );
};

export class InsurancePickerViewBase extends Component {
    static propTypes = {
        directoryId: PropTypes.string,
        gaCategory: PropTypes.string,
        handleAutocompleteChange: PropTypes.func.isRequired,
        isChange: PropTypes.bool,
        isErrorState: PropTypes.bool,
        isOpen: PropTypes.bool,
        onFocus: PropTypes.func,
        registerGoogleAnalyticsCategory: PropTypes.func.isRequired,
        registerLoggingSection: PropTypes.func.isRequired,
        section: PropTypes.string,
        showScanInsuranceCardButton: PropTypes.bool,
        showValidationBackground: PropTypes.bool,
        textBoxChange: PropTypes.func,
    };

    static defaultProps = {
        onFocus: noop,
        showScanInsuranceCardButton: false,
        textBoxChange: noop,
    };

    willFocus = false;

    componentDidMount() {
        this.props.registerGoogleAnalyticsCategory(this.props.gaCategory);
        this.props.registerLoggingSection(this.props.section);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.focusCounter !== this.props.focusCounter) {
            this.willFocus = true;
        }

        if (this.willFocus || (this.dropdown && window.innerWidth > Breakpoints.small)) {
            this.focus();
            this.willFocus = false;
        } else {
            this.inputRef.blur();
        }
    }

    render() {
        return (
            <InsurancePicker
                data-prod-guids="insurance-picker-wrapper:[d1d2f34b]"
                errorState={this.props.isErrorState}
            >
                <InsurancePickerTextbox
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    data-prod-guids="insurance-picker-textbox:[d1d2f34b]"
                    data-test="insurance-picker-textbox"
                    errorState={this.props.isErrorState}
                    icon={this.getIcon()}
                    innerRef={instance => { this.inputRef = instance; }}
                    isOpen={this.props.isOpen || this.props.isChange}
                    name={null}
                    onChange={this.handleChange}
                    onClick={this.handleTextBoxClick}
                    onFocus={this.handleTextBoxFocus}
                    placeholder={this.getPlaceholder()}
                    spellCheck="false"
                    type="text"
                    value={this.getTextBoxValue()}
                >
                    {this.maybeRenderDropdown()}
                </InsurancePickerTextbox>
                {this.renderSelectedState()}
            </InsurancePicker>
        );
    }

    renderSelectedState() {
        const {
            carrier,
            hideValidation,
            insuranceAcceptanceLoading,
            insuranceIsAccepted,
            isErrorState,
            isOpen,
            showValidationBackground,
        } = this.props;

        const flowCompleted = !isOpen && !isErrorState && carrier;

        if (flowCompleted) {
            return (
                <SelectedState
                    hideValidation={hideValidation}
                    insuranceAcceptanceLoading={insuranceAcceptanceLoading}
                    insuranceIsAccepted={insuranceIsAccepted}
                    showValidationBackground={showValidationBackground}
                />
            );
        }
    }

    getIcon() {
        const { isErrorState } = this.props;

        if (!isErrorState) {
            return;
        }

        return ErrorIcon;
    }

    getPlaceholder() {
        return getPlaceholder(this.props.tab);
    }

    getTextBoxValue() {
        const {
            carrier,
            isErrorState,
            isOpen,
            plan,
            typedValue,
        } = this.props;

        const shouldShowErrorState = !isOpen && isErrorState;
        const flowCompleted = !isOpen && !isErrorState && carrier;

        if (flowCompleted) {
            if (plan && plan.name) {
                const shortenName = (name, otherLength) => {
                    let maxLength = 16;
                    if (otherLength < maxLength) {
                        maxLength += (maxLength - otherLength);
                    }

                    return (name.length > maxLength
                        ? `${name.substring(0, maxLength - 3)}...`
                        : name);
                };

                const carrierName = shortenName(carrier.name, plan.name.length);
                const planName = shortenName(plan.name, carrier.name.length);

                return `${carrierName} - ${planName}`;
            } else {
                return `${carrier.name}`;
            }
        } else if (shouldShowErrorState) {
            if (typedValue) {
                return typedValue;
            } else if (!carrier || carrier.id === CARRIER.BLUE_CROSS_BLUE_SHIELD) {
                return 'choose insurance';
            } else {
                return 'choose plan';
            }
        }

        return `${typedValue}`;
    }

    maybeRenderDropdown() {
        const {
            isOpen,
            showScanInsuranceCardButton,
        } = this.props;

        if (!isOpen) {
            return;
        }

        return (
            <InsurancePickerDropdown
                ref={instance => { this.dropdown = instance; }}
                showScanInsuranceCardButton={showScanInsuranceCardButton}
            />
        );
    }

    handleTextBoxFocus = () => {
        this.props.onFocus();

        if (!this.willFocus) {
            this.props.openDropdown();
        }
    };

    handleTextBoxClick = () => {
        if (!this.willFocus) {
            this.props.openDropdown();
        }
    };

    handleClickOutside = () => this.props.closeDropdown();

    handleChange = e => {
        this.props.handleAutocompleteChange(e.target.value);
        this.props.textBoxChange(e);
    }

    onKeyChange = e => {
        const { isOpen } = this.props;

        if (isOpen && e.key === 'Enter') {
            // the enter keypress should not be allowed to propagate into a submit cascade when
            // the dropdown is visible because the new value will not have been applied yet to the
            // input box; a second enter press after the dropdown is closed is A-OK to submit
            // (same behavior as the Universal Search)

            e.stopPropagation();
            e.preventDefault();

            this.willFocus = true;
            this.inputRef.focus();
        } else if (e.key === 'Escape' || e.key === 'Tab') {
            this.props.closeDropdown();
        }
    }

    focus() {
        this.inputRef.focus();
    }
}


const InsurancePicker = styled.div`
    position: relative;
`;

const InsurancePickerTextbox = styled(Textbox)`
    border: ${INPUT_BORDER_WIDTH}px solid ${white};
    color: ${p => p.errorState ? `${purple} !important` : navy};
    display: ${p => p.isOpen ? 'none' : ''};
    font: 15px ${FontFamily.SharpSansSemibold};
    height: ${INPUT_HEIGHT}px;
    padding-left: ${p => p.errorState ? 45 : NON_INPUT_X_AFFORDANCE_MOBILE}px;
    padding-right: ${NON_INPUT_X_AFFORDANCE_MOBILE}px;

    &:focus {
        border-color: ${white};
    }

    ${breakpoint.small} {
        display: ${p => p.isOpen ? 'inline' : ''};
        padding-left: ${p => p.errorState ? 45 : NON_INPUT_X_AFFORDANCE_DESKTOP}px;
        padding-right: ${NON_INPUT_X_AFFORDANCE_DESKTOP}px;
    }

    &::placeholder {
        color: ${grey75};
    }

    &:focus::placeholder {
        color: ${grey25};
    }
`;

const StyledTooltip = styled(Tooltip)`
    color: ${navy};
    font: 13px/20px ${FontFamily.SharpSansMedium};
    min-width: 175px;
    z-index: 10;

    &::after,
    &::before {
        left: 12px !important;
    }
`;

export const InsurancePickerView =
    detectClickOutside(
        handleKeyChange({ keyChangeType: 'keydown' })(
            InsurancePickerViewBase
        )
    );

export const InsurancePickerViewPrimitives = {
    InsurancePickerTextbox,
};
